import {
  Button,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

const saveConfigurationSchema = z.object({
  name: z.string().min(1, 'Il nome è obbligatorio'),
  description: z.string().optional(),
});

type SaveConfigurationFormValues = z.infer<typeof saveConfigurationSchema>;

interface SaveConfigurationModalProps {
  opened: boolean;
  onClose: () => void;
  onSave: (name: string, description: string) => void | Promise<void>;
  initialName?: string;
  initialDescription?: string;
  loading?: boolean;
}

export function SaveConfigurationModal({
  opened,
  onClose,
  onSave,
  initialName,
  initialDescription,
  loading = false,
}: SaveConfigurationModalProps) {
  const form = useForm<SaveConfigurationFormValues>({
    resolver: zodResolver(saveConfigurationSchema),
    defaultValues: {
      name: initialName || '',
      description: initialDescription || '',
    },
  });

  const handleClose = () => {
    form.reset({
      name: initialName || '',
      description: initialDescription || '',
    });
    onClose();
  };

  const handleSubmit = form.handleSubmit(async values => {
    await onSave(values.name, values.description || '');
    handleClose();
  });

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title='Salva Configurazione'
      centered
      size='md'
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
    >
      <form onSubmit={handleSubmit}>
        <Stack gap='md'>
          <Text size='sm' c='dimmed'>
            Salva questa configurazione come template per riutilizzarla in
            futuro.
          </Text>

          <TextInput
            label='Nome Configurazione'
            placeholder='Inserisci il nome della configurazione'
            required
            {...form.register('name')}
            error={form.formState.errors.name?.message}
            disabled={loading}
          />

          <Textarea
            label='Descrizione'
            placeholder='Inserisci una descrizione (opzionale)'
            autosize
            minRows={3}
            {...form.register('description')}
            error={form.formState.errors.description?.message}
            disabled={loading}
          />

          <Group justify='flex-end' gap='xs' mt='md'>
            <Button variant='subtle' onClick={handleClose} disabled={loading}>
              Annulla
            </Button>
            <Button type='submit' loading={loading}>
              Salva
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
